import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import { calculateBmi } from '@/parcours/flows/shared/helper';
import TextArea from '@/molecules/TextArea.vue';
import defaultValidators from '@/helpers/default-validators';
import SpecialistSelector from '@/parcours/components/specialistSelector.vue';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import { getPatientAge } from '@/helpers/patient';
import { MODAL_CONTENT_BREAST_CANCER } from '@/parcours/flows/breastCancer/modalContentBreastCancer';

const { update, getById } = usePreventionParcours('/cms/coordinator-patient-breast-cancer-infos');

export const breastCancerFlow = withGlobalFlow({
  id: 'breastCancerFlow',

  basePath: '/breastCancerFlow/flow',

  exitRoute: '/breastCancerFlow',

  async onInit({ route }) {
    const id = route.params.reservationId;
    const data = await getById(id);
    return {
      ...data,
      ...(data?.imc === undefined && {
        imc: calculateBmi(data?.height, data?.weight),
      }),
    };
  },

  steps: {
    modifiableRiskFactorsList: {
      type: 'component',
      component: ListInfo,
      title: 'Facteurs modifiables',
      props: (data: any) => {
        const age = getPatientAge(data?.flowData?.patient);
        return {
          data,
          config: [
            { key: 'height', label: 'Taille', suffix: 'cm' },
            { key: 'weight', label: 'Poids', suffix: 'kg' },
            { key: 'imc', label: 'IMC' },
            { label: 'Âge', value: age != null ? Math.floor(age) : null, suffix: 'ans' },
            { key: 'balanced_diet', label: 'Alimentation variée', type: 'boolean' },
            { key: 'exercise', label: 'Activité physique régulière', type: 'boolean' },
            { key: 'frequent_alcohol', label: "Consommation régulière d'alcool", type: 'boolean' },
            { key: 'smoking', label: 'Tabagisme', type: 'boolean' },
          ],
        };
      },
    },
    noModifiableRiskFactorsList: {
      type: 'component',
      component: ListInfo,
      title: 'Facteurs non-modifiables personnels',
      props: (data: any) => ({
        data,
        config: [
          {
            key: 'participant_breast_cancer',
            label: 'Patient(e) déjà atteinte du cancer du sein',
            type: 'boolean',
          },
          {
            key: 'participant_breast_sickness',
            label: "Antécédents d'affections du sein (hyperplasie atypique ou affection bénigne)",
            type: 'boolean-string',
          },
          {
            key: 'participant_radiation_exposure',
            label: 'Exposé(e) à des radiations médicales',
            type: 'boolean-string',
          },
        ],
      }),
    },
    nonModifiableFamilyRiskFactors: {
      type: 'component',
      component: ListInfo,
      title: 'Facteurs non-modifiables familiaux',
      props: (data: any) => ({
        data,
        config: [
          {
            key: 'relative_breast_cancer',
            label: 'Membre de la famille atteinte par le cancer du sein avant 40 ans',
            type: 'boolean-string',
          },
          {
            key: 'relative_breast_cancer_profile',
            label: 'Profil du membre de la famille',
            showIf: (data: any) => data.relative_breast_cancer === true,
          },
          {
            key: 'relative_family_gene_mutation',
            label: 'Mutation identifiée dans la famille du gène BRCA1 ou BRCA2',
            type: 'boolean-string',
          },
          {
            key: 'relative_ovaries_cancer',
            label: "Antécédent familial de cancer de l'ovaire",
            type: 'boolean-string',
          },
        ],
        actions: [
          {
            variant: 'button',
            label: (flowData: any) => {
              const score = flowData?.elsenger_score ?? 0;
              return score >= 5
                ? "Score d'Eisinger : supérieur à 5"
                : score >= 3
                  ? "Score d'Eisinger : supérieur à 3"
                  : "Score d'Eisinger : inférieur à 3";
            },
            buttonType: (flowData: any) => {
              const score = flowData?.elsenger_score ?? 0;
              return score >= 5 ? 'destructive' : score >= 3 ? 'primary' : 'secondary';
            },
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: "Score d'Eisinger",
                content: MODAL_CONTENT_BREAST_CANCER.EISINGER,
              });
            },
          },
        ],
      }),
    },
    recommandation: {
      id: 'other_specialists',
      type: 'component',
      title: 'Recommandations',
      description:
        "Je recommande au patient d'aller consulter le(s) professionnel(s) de santé suivant(s):",
      component: SpecialistSelector,
      buttonNextTitle: 'Valider',
      props: {
        specialists: [
          { name: 'Gynécologue', value: 'Gynécologue' },
          { name: 'Médecin généraliste', value: 'Médecin généraliste' },
          { name: 'Sage-femme', value: 'Sage-femme' },
          { name: 'Radiologue', value: 'Radiologue' },
        ],
      },
    },
    lastComment: {
      type: 'form',
      title: 'Un dernier commentaire ?',
      description: 'Autre commentaire ou recommandation pour le patient',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'other_recommendations',
            type: 'textarea',
            placeholder: 'Votre commentaire ...',
            as: TextArea,
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },
  },

  transitions: {
    modifiableRiskFactorsList: { next: 'noModifiableRiskFactorsList' },
    noModifiableRiskFactorsList: { next: 'nonModifiableFamilyRiskFactors' },
    nonModifiableFamilyRiskFactors: { next: 'recommandation' },
    recommandation: { next: 'lastComment' },
    lastComment: { submit: true },
  },

  async onSubmit(data) {
    await update(data?.id, data);
  },
});
